import React, { useState, useContext } from 'react';
import { useSwipeable } from 'react-swipeable';
import { Container, Button } from 'react-bootstrap';
import GroupProfileCard from './GroupProfileCard';
import matchingService from '../services/matchingService';
import { AuthContext } from '../contexts/AuthContext';

const SwipeableGroupProfiles = ({ groups, listingId, providerId }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [likedGroups, setLikedGroups] = useState([]);
  const [dislikedGroups, setDislikedGroups] = useState([]);
  const [deltaX, setDeltaX] = useState(0);
  const [swipeDirection, setSwipeDirection] = useState(null);
  const { auth } = useContext(AuthContext);

  const handleSwipe = async (direction) => {
    const currentGroup = groups[currentIndex];
    if (!currentGroup) return;
    
    const swipeDir = direction === 'Right' ? 'right' : 'left';
    
    try {
      // Only send swipe to backend if we have auth and all required data
      if (auth?.token && listingId && providerId) {
        await matchingService.groupSwipe(
          providerId, // This should be providerProfileId
          listingId,
          swipeDir
        );
      } else {
        console.warn('Missing required data for backend swipe:', { 
          authToken: !!auth?.token, 
          listingId, 
          providerId 
        });
      }
      
      // Update local state
      if (direction === 'Right') {
        setLikedGroups((prev) => [...prev, currentGroup]);
      } else if (direction === 'Left') {
        setDislikedGroups((prev) => [...prev, currentGroup]);
      }
      
      setSwipeDirection(direction);
      setTimeout(() => {
        setSwipeDirection(null);
        setCurrentIndex((prev) => prev + 1);
        setDeltaX(0);
      }, 300);
    } catch (error) {
      console.error('Failed to register group swipe:', error);
      // Still update the UI even if the API call fails
      if (direction === 'Right') {
        setLikedGroups((prev) => [...prev, currentGroup]);
      } else if (direction === 'Left') {
        setDislikedGroups((prev) => [...prev, currentGroup]);
      }
      
      setSwipeDirection(direction);
      setTimeout(() => {
        setSwipeDirection(null);
        setCurrentIndex((prev) => prev + 1);
        setDeltaX(0);
      }, 300);
    }
  };

  const handlers = useSwipeable({
    onSwipedLeft: () => handleSwipe('Left'),
    onSwipedRight: () => handleSwipe('Right'),
    onSwiping: ({ deltaX }) => setDeltaX(deltaX),
    onSwiped: () => setDeltaX(0),
    preventScrollOnSwipe: true,
    trackMouse: true,
  });

  if (currentIndex >= groups.length) {
    // All group postings swiped; now prompt the CRNA to schedule their consultation.
    return (
      <Container className="text-center my-5">
        <h3> All group postings reviewed!</h3>
        <p>{likedGroups.length} Groups of Interest</p>
        <p>{dislikedGroups.length} Not Suitable</p>
        <div className="mt-4">
          <Button
            variant="primary"
            size="lg"
            className="rounded-pill px-4 py-2"
            href="/consultation"
          >
            Schedule Your Dream Shift Consultation
          </Button>
          <p className="text-muted mt-2" style={{ fontSize: '0.95rem' }}>
            Fill out our consultation form to unlock your dream shifts with top groups.
          </p>
        </div>
      </Container>
    );
  }

  const rotation = deltaX / 20;
  const isSwipingRight = deltaX > 50;
  const isSwipingLeft = deltaX < -50;

  const getTopCardStyle = () => {
    const flyAway = swipeDirection
      ? swipeDirection === 'Right'
        ? 'translateX(100vw) rotate(45deg)'
        : 'translateX(-100vw) rotate(-45deg)'
      : `translateX(${deltaX}px) rotate(${rotation}deg)`;

    return {
      position: 'absolute',
      width: '100%',
      maxWidth: '360px',
      transition: swipeDirection ? 'transform 0.3s ease-out' : 'none',
      transform: flyAway,
      cursor: 'grab',
      zIndex: groups.length - currentIndex,
    };
  };

  const getStackedCardStyle = (indexOffset) => ({
    position: 'absolute',
    width: '100%',
    maxWidth: '360px',
    transform: `scale(${1 - 0.05 * indexOffset}) translateY(${12 * indexOffset}px)`,
    opacity: 1 - 0.1 * indexOffset,
    transition: 'transform 0.3s ease-out, opacity 0.3s ease-out',
    zIndex: groups.length - currentIndex - indexOffset,
  });

  const getOverlayStyle = () => ({
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: isSwipingRight
      ? 'rgba(40,167,69,0.2)'
      : isSwipingLeft
      ? 'rgba(220,53,69,0.2)'
      : 'transparent',
    borderRadius: '12px',
    pointerEvents: 'none',
    transition: 'background-color 0.2s',
  });

  return (
    <Container
      className="my-4 d-flex flex-column align-items-center position-relative"
      style={{ height: '500px' }}
    >
      <div
        {...handlers}
        style={{
          position: 'relative',
          width: '100%',
          maxWidth: '360px',
          height: '100%',
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        {groups.slice(currentIndex, currentIndex + 3).map((group, idx) => {
          if (idx === 0) {
            return (
              <div key={currentIndex + idx} style={getTopCardStyle()}>
                <GroupProfileCard profile={group} />
                <div style={getOverlayStyle()} />
              </div>
            );
          }
          return (
            <div key={currentIndex + idx} style={getStackedCardStyle(idx)}>
              <GroupProfileCard profile={group} />
            </div>
          );
        })}
      </div>
    </Container>
  );
};

export default SwipeableGroupProfiles;
